import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import { FORMAT_TYPES } from "../index.js";

const KEY_CODES = {
  arrowUp: 38,
  arrowDown: 40
};

SuggestionList.propTypes = {
  editorRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  acceptedKeyStrokes: PropTypes.objectOf(PropTypes.func),
  insertBlock: PropTypes.func,
  onEnterHandler: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isEditorFocussed: PropTypes.bool,
  onSlashCommandSelect: PropTypes.func,
  allowSlashCommand: PropTypes.bool
};

export function SuggestionList({
  editorRef,
  acceptedKeyStrokes,
  insertBlock,
  onEnterHandler,
  onOpen = () => {},
  onClose = () => {},
  isEditorFocussed = true,
  onSlashCommandSelect,
  allowSlashCommand
}) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editor = editorRef.current || {};
  const [showPopup, setShowPopup] = useState(false);
  const [text, setText] = useState("");
  const [bounds, setBounds] = useState({});
  const [type, setType] = useState("");
  const suggestionListRef = useRef(null);

  const getPrefixText = useCallback(
    function prefixText() {
      const [range] = editor.selection.getRange();
      if (range == null) return "";
      const [leaf, offset] = editor.getLeaf(range.index);
      try {
        if (offset > 0) return leaf.value()?.slice(0, offset);
        return leaf.value()?.slice(0);
      } catch (e) {
        return "";
      }
    },
    [editor]
  );

  const enterHandler = useCallback(
    function updateKeyBindings() {
      if (editor.keyboard) {
        const mentionIndex = editor.keyboard.bindings["13"].findIndex(
          (item) => item.type === "mention"
        );
        if (mentionIndex > -1) {
          editor.keyboard.bindings["13"].splice(mentionIndex, 1);
        }
        if (showPopup) {
          editor.keyboard.bindings["13"].unshift({
            key: "Enter",
            type: "mention",
            shiftKey: null,
            handler: () => {
              const list =
                suggestionListRef.current &&
                suggestionListRef.current.getList();
              if (list && (!list.length || !showPopup)) return onEnterHandler();
              else if (!showPopup) return onEnterHandler();
              else return !showPopup;
            }
          });
        }
      }
    },
    [editor.keyboard, onEnterHandler, showPopup]
  );

  const onSelect = useCallback(
    function selectHandler(data) {
      if (!data) {
        setShowPopup(false);
        onClose();
        return null;
      }
      if (type === "@") {
        let inlineData = {};
        let Mention = {};
        if (data.Kind !== "Tagged") {
          Mention.Id = data._id;
          Mention.Name = data.Name;
          if (data.Kind) {
            Mention.Kind = data.Kind;
          } else if (data.Type) {
            Mention.Type = data.Type;
          }
          inlineData.Mention = Mention;
          inlineData.text = text;
        } else {
          for (let i = 0; i < data.UserList.length; i++) {
            let user = data.UserList[i];
            Mention.Id = user._id || user.Id;
            Mention.Name = user.Name;
            if (user.Kind) {
              Mention.Kind = user.Kind;
            } else if (user.Type) {
              Mention.Type = user.Type;
            }
            inlineData.Mention = Mention;
            inlineData.text = i === 0 ? text : "";
            insertBlock(FORMAT_TYPES.MENTION, inlineData, {
              spaceRequired: data.spaceRequired || true,
              removeTypedText: true
            });
          }
        }
        if (data.Kind !== "Tagged")
          insertBlock(FORMAT_TYPES.MENTION, inlineData, {
            spaceRequired: data.spaceRequired || true,
            removeTypedText: true
          });
      } else if (type === "#") {
        let inlineData = {};
        let HashTag = {};
        HashTag.Id = data._id;
        HashTag.Name = `#${data._id}`;
        inlineData.HashTag = HashTag;
        inlineData.text = text;
        insertBlock(FORMAT_TYPES.HASHTAG, inlineData, {
          spaceRequired: data.spaceRequired || false,
          removeTypedText: true
        });
      } else if (type === "/" && allowSlashCommand) {
        const formatType = data;
        onSlashCommandSelect(formatType, text);
      }
      reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insertBlock, text, type, allowSlashCommand]
  );

  const handleKeyDown = useCallback(
    function keyDownHandler(e) {
      if (e.keyCode === KEY_CODES.arrowUp) {
        if (showPopup) {
          e.preventDefault();
        }
      } else if (e.keyCode === KEY_CODES.arrowDown) {
        if (showPopup) {
          e.preventDefault();
        }
      }
    },
    [showPopup]
  );

  const onTextChange = useCallback(
    function textChangeHandler(_delta, _old) {
      const prefix = getPrefixText();
      const splittedPrefix = prefix.replaceAll("\u200b", "").split(" ");
      const lastWord = splittedPrefix[splittedPrefix.length - 1];
      if (showPopup) {
        if (acceptedKeyStrokes[lastWord]) {
          setType(lastWord);
          const range = editor.getSelection(true);
          if (!range) return null;
          const bounds = editor.getBounds(range.index);
          search(lastWord);
          setBounds(bounds);
        } else {
          const parts = lastWord.split(type);
          if (
            lastWord.charAt(lastWord.length - 1) === " " ||
            parts.length <= 1
          ) {
            reset();
          } else {
            search(type + parts.join(""));
          }
        }
      } else if (acceptedKeyStrokes[lastWord]) {
        setType(lastWord);
        const range = editor.getSelection(true);
        if (!range) return null;
        const bounds = editor.getBounds(range.index);
        search(lastWord);
        setBounds(bounds);
        setShowPopup(true);
        onOpen();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acceptedKeyStrokes, editor, getPrefixText, showPopup, text]
  );

  function search(text) {
    setText(text);
  }

  function reset() {
    setBounds({});
    setShowPopup(false);
    onClose();
    setText("");
  }

  useEffect(
    function updateEnterHandler() {
      enterHandler();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showPopup]
  );

  useEffect(
    function registerListeners() {
      if (editor && editor.on) {
        editor.on("text-change", onTextChange);
        editor.root.addEventListener("keydown", handleKeyDown);
        return () => {
          editor.off("text-change", onTextChange);
          editor.root.removeEventListener("keydown", handleKeyDown);
        };
      }
    },
    [editor, onTextChange, handleKeyDown, enterHandler, showPopup]
  );

  // NOTE: In Portal runtime, dont show @ mention
  if (window.accountDetails?.IsPortal) return null;

  if (!showPopup) return null;
  if (!isEditorFocussed) return null;
  const Component = acceptedKeyStrokes[type];

  if (text) {
    return (
      <div className="mentioncontainer" tabIndex="-1">
        <Component
          ref={suggestionListRef}
          text={text.replace(/@|#|\/|{{/g, "")}
          onSelect={onSelect}
          style={{
            top: bounds.top + 20,
            left: bounds.left
          }}
          bounds={bounds}
          type={type}
          close={reset}
        />
      </div>
    );
  }
  return null;
}
